/**
 * useResponsive Hook
 * 
 * Shared responsive utilities and breakpoint detection.
 */

import { useState, useEffect, useCallback } from 'react';
import { breakpoints } from '../styles/tokens';

export type Breakpoint = keyof typeof breakpoints;

interface ResponsiveState {
  width: number;
  height: number;
  breakpoint: Breakpoint;
  isMobile: boolean;       // < 768px (xs, sm, md)
  isTablet: boolean;       // 768-1023px (lg)
  isDesktop: boolean;      // >= 1024px (xl+)
  isLargeDesktop: boolean; // >= 1280px (2xl+)
  isTouchDevice: boolean;
}

const getBreakpoint = (width: number): Breakpoint => {
  if (width >= breakpoints['3xl']) return '3xl';
  if (width >= breakpoints['2xl']) return '2xl';
  if (width >= breakpoints.xl) return 'xl';
  if (width >= breakpoints.lg) return 'lg';
  if (width >= breakpoints.md) return 'md';
  if (width >= breakpoints.sm) return 'sm';
  return 'xs';
};

const isTouchDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

export const useResponsive = (): ResponsiveState => {
  const [state, setState] = useState<ResponsiveState>(() => {
    if (typeof window === 'undefined') {
      return {
        width: 1024,
        height: 768,
        breakpoint: 'xl',
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        isLargeDesktop: false,
        isTouchDevice: false,
      };
    }

    const width = window.innerWidth;
    const height = window.innerHeight;

    return {
      width,
      height,
      breakpoint: getBreakpoint(width),
      isMobile: width < breakpoints.lg,
      isTablet: width >= breakpoints.lg && width < breakpoints.xl,
      isDesktop: width >= breakpoints.xl,
      isLargeDesktop: width >= breakpoints['2xl'],
      isTouchDevice: isTouchDevice(),
    };
  });

  const handleResize = useCallback(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;

    setState({
      width,
      height,
      breakpoint: getBreakpoint(width),
      isMobile: width < breakpoints.lg,
      isTablet: width >= breakpoints.lg && width < breakpoints.xl,
      isDesktop: width >= breakpoints.xl,
      isLargeDesktop: width >= breakpoints['2xl'],
      isTouchDevice: isTouchDevice(),
    });
  }, []);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 50);
    };

    window.addEventListener('resize', debouncedResize);
    window.addEventListener('orientationchange', handleResize);
    handleResize();

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', debouncedResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [handleResize]);

  return state;
};

export default useResponsive;

